import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Button,
  Box,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  getPendingPhotoVerifications,
  verifyUser,
  denyUserVerification,
} from "../../context/User";
import ImageLoader from "../dashboard/ImageLoader";

const VerifyAccounts = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // 👈 for modal
  const [open, setOpen] = useState(false);

  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getPendingPhotoVerifications();
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const formatDate = (createdAt) => {
    try {
      const date = new Date(createdAt);
      if (isNaN(date.getTime())) throw new Error("Invalid timestamp");
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return "Invalid date";
    }
  };

  const handleApprove = async (id) => {
    try {
      setActionLoading(true);
      await verifyUser(id);
      await fetchData();
      setOpen(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async (id) => {
    try {
      setActionLoading(true);
      await denyUserVerification(id);
      await fetchData();
      setOpen(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReview = (verification) => {
    setSelectedUser(verification);
    setOpen(true);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={4}
        color={theme.palette.text.primary}
      >
        Account Verifications
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: "50vh" }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : data.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            mt: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6" color="textSecondary">
            There are no pending verifications at this time
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            overflowX: "auto",
          }}
        >
          <Table>
            <TableHead
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "#2e281f"
                    : theme.palette.primary.main,
              }}
            >
              <TableRow>
                {[
                  "User",
                  "Email",
                  "Status",
                  "Submitted",
                  "Photo",
                  "Action",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      color: theme.palette.common.white,
                      fontWeight: "bold",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((verification) => (
                <TableRow
                  key={verification._id}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <TableCell>{verification.user.username}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {verification.user.email}
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={
                        verification.status === "pending"
                          ? "warning.main"
                          : verification.status === "approved"
                          ? "success.main"
                          : "error.main"
                      }
                      fontWeight="medium"
                      p={0}
                      m={0}
                    >
                      {verification.status.charAt(0).toUpperCase() +
                        verification.status.slice(1)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatDate(verification.createdAt)}
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: `2px solid ${theme.palette.divider}`,
                        backgroundColor: "#2e281f",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ImageLoader
                        source={{ uri: verification.image }}
                        alt="Verification"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleReview(verification)}
                      sx={{ minWidth: 90 }}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* === Review Modal === */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedUser && (
          <>
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Review Verification
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  py: 2,
                }}
              >
                {/* Image with hover overlay */}
                <Box
                  sx={{
                    position: "relative",
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: `3px solid ${theme.palette.divider}`,
                    cursor: "pointer",
                    "&:hover .overlay": {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setImagePreviewOpen(true)}
                >
                  <ImageLoader
                    source={{ uri: selectedUser.image }}
                    alt="Verification"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: 0,
                      transition: "opacity 0.3s",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      View Full
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h6" fontWeight="bold">
                  {selectedUser.user.fullname || selectedUser.user.username}
                </Typography>
                <Typography color="text.secondary" p={0} m={0}>
                  {selectedUser.user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" p={0} m={0}>
                  Location:{" "}
                  {selectedUser.user.location?.geoLocation || "Unknown"}
                </Typography>

                <Box sx={{ mt: 2, width: "100%" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Account Status:{" "}
                    <strong style={{ color: theme.palette.warning.main }}>
                      {selectedUser.status.toUpperCase()}
                    </strong>
                  </Typography>
                  <Typography variant="subtitle2">
                    Joined: {formatDate(selectedUser.user.createdAt)}
                  </Typography>
                  <Typography variant="subtitle2">
                    Last Seen: {formatDate(selectedUser.user.lastSeen)}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeny(selectedUser._id)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Reject"
                )}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApprove(selectedUser._id)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Approve"
                )}
              </Button>
            </DialogActions>

            {/* Image Preview Dialog */}
            <Dialog
              open={imagePreviewOpen}
              onClose={() => setImagePreviewOpen(false)}
              maxWidth="xs"
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  p: 2,
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageLoader
                  source={{ uri: selectedUser.image }}
                  alt="full preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <Button
                  onClick={() => setImagePreviewOpen(false)}
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  Close
                </Button>
              </Box>
            </Dialog>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default VerifyAccounts;
